import type { Campaign } from '~/types/graphql';

import {
  AnalyticCampaignProvider,
  useAnalyticCampaignGroupContext,
} from '~/modules/campaign-analytics';
import {
  BannerUnpublishTemplates,
  useCampaignTemplateVariantInfo,
} from '~/modules/campaign-template';
import { useCGTemplateContext } from '~/modules/campaign-group-template/contexts';
import { CGTemplatePairVariants } from './CGTemplatePairVariants';
import { useCampaignNavigate } from '~/modules/campaign/hooks';
import { ICampaignType } from '~/modules/campaign/types';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { useThemePageStore } from '~/stores/themePage';
import { useWindowSize } from '~/presentation/common';
import { useHeadingCGTemplatePair } from '../hooks';
import { Box, Scrollable } from '@shopify/polaris';
import { INavigateKey } from '~/types/navigate';
import { GTable } from '~/presentation/ui';
import { useMemo } from 'react';

export const CGTemplatePairItem = ({ data }: Readonly<{ data: Campaign }>) => {
  const { windowSize } = useWindowSize();
  const { onEditCampaign, onAnalyticsCampaign } = useCampaignNavigate();
  const { analyticMode } = useAnalyticCampaignGroupContext();
  const { campaignGroup } = useCGTemplateContext();

  const dataThemePages = useThemePageStore((state) => state.themePages);
  const themePages = useMemo(() => dataThemePages, [dataThemePages]);
  const variantInfos = useCampaignTemplateVariantInfo(data, themePages);
  const listHeader = useHeadingCGTemplatePair(data);

  const handleNavigateToDetail = () => {
    if (windowSize.width <= 768) return;
    if (data.status === CAMPAIGN_STATUS.DRAFT) {
      onEditCampaign(data);
      return;
    }

    onAnalyticsCampaign({
      id: data.id,
      type: ICampaignType.TEMPLATE,
    });
  };

  return (
    <AnalyticCampaignProvider
      campaign={data}
      isQueryPreviousData={false}
      analyticMode={analyticMode}
    >
      <Scrollable vertical={false} horizontal scrollbarWidth="thin" className="max-w-[100%]">
        <Box minWidth="1120px">
          <GTable
            classRemoved
            listHeader={listHeader}
            paddingTable={{ paddingBlockStart: '0' }}
            onClick={handleNavigateToDetail}
            items={[
              {
                title: data.name,
                status: data.name,
                description: data.name,
                items: ['Test name'],
              },
            ]}
          >
            {variantInfos && <CGTemplatePairVariants campaign={data} variants={variantInfos} />}
            <Box paddingBlockStart="400">
              <BannerUnpublishTemplates
                campaign={data}
                targetCampaign={campaignGroup}
                isBannerInCard={true}
                screen={INavigateKey.CampaignGroupAnalytic}
              />
            </Box>
          </GTable>
        </Box>
      </Scrollable>
    </AnalyticCampaignProvider>
  );
};
